<template>
  <div class="ce-wrapper">
    <div v-if="editMode" :class="'ce ce-' + content.type + ' ce-edit-mode'">
      <div class="edit-header d-flex align-items-center">
        <h5 class="mb-0">
          <span class="text-success"><i class="fas fa-video"></i></span> Video
        </h5>
        <CeButtonGroup
          v-on:on-remove-click="onRemoveClick"
          v-on:on-arrow-up-click="onArrowUp"
          v-on:on-arrow-down-click="onArrowDown"
        />
      </div>
      <b-form-group
        label="Rubrik"
        :for="'ceVideoHeadline' + content.id"
        label-sr-only
      >
        <b-form-input
          :value="content.headline"
          @change="updateHeadline"
          id="headline"
          placeholder="Rubrik för video...(valfritt)"
        ></b-form-input>
        <!-- <b-form-input :value="content.headline" :id="'ceVideoHeadline'+content.id" placeholder="Skriv en rubrik..."></b-form-input> -->
      </b-form-group>
      <b-form-group label="Ladda upp video" :for="'ceVideo' + content.id">
        <b-form-file
          v-model="video"
          accept="video/*"
          :id="'ceVideo' + content.id"
          browse-text="Välj video"
          placeholder="Klicka och välj video..."
        >
          <template slot="file-name" slot-scope="{ names }">
            <b-badge variant="primary">
              {{ names[0] }}
            </b-badge>
            <b-badge v-if="names.length > 1" variant="dark" class="ml-1">
              + {{ names.length - 1 }} More files
            </b-badge>
          </template>
        </b-form-file>

        <!-- Uppdatera så att drag and drop funkar för videon och ta bort dessa när det funkar-->
        <!-- <p v-if="content.video">Uppladdad video: {{ content.video.title.slice(0, -4) }}</p> -->
      </b-form-group>

      <!-- Om videon håller på att bearbetas -->
      <div v-if="state == 'Processing'">
        <b-alert
          v-if="!hasVideoBeenUploaded && !areThereErrorsUploadingVideo"
          show
          variant="warning"
          class="mb-2"
        >
          <!-- visas endast om portföljen är satt i läsläge -->
          <h4 class="alert-heading">
            <i class="fas fa-exclamation-triangle"></i> Din video laddas upp..
          </h4>
          <div class="alert-body text-center">
            <p>
              Vänligen lämna inte denna sidan innan din video har laddat
              färdigt. Detta kan ta en liten stund om det är en lång videofilm
              men du kan fortsätta att redigera artikeln under tiden.
            </p>
            <b-spinner
              variant="warning"
              class="my-2"
              style="width: 3rem; height: 3rem"
              label="Text Centered"
            />
          </div>
        </b-alert>
        <b-alert
          v-else-if="hasVideoBeenUploaded && !areThereErrorsUploadingVideo"
          show
          variant="primary"
          class="mb-2"
        >
          <h4 class="alert-heading">
            <i class="fas fa-exclamation-triangle"></i> Din video bearbetas nu..
          </h4>
          <div class="alert-body text-center">
            <p>
              Detta kan ta en liten stund men det går nu bra att spara eller
              lämna den här sidan under tiden. När videon är klar kommer du att
              se den här.
            </p>
            <b-spinner
              variant="primary"
              class="my-2"
              style="width: 3rem; height: 3rem"
              label="Text Centered"
            />
          </div>
        </b-alert>
        <b-alert
          v-else-if="!hasVideoBeenUploaded && areThereErrorsUploadingVideo"
          show
          variant="danger"
          class="mb-2"
        >
          <h4 class="alert-heading">
            <i class="fas fa-exclamation-triangle"></i> Något gick fel...
          </h4>
          <div class="alert-body text-center">
            <p>Din video kunde inte laddas upp.</p>
          </div>
        </b-alert>
      </div>

      <!-- Om videon är uppladdad och klar, visa en liten förhandsgranskning -->
      <b-form-group v-if="videoSource && state == 'Finished'">
        <div class="ce-list">
          <label>Uppladdad fil</label>

          <div class="ce-list-item">
            <div class="ce-list-item-prefix align-items-start">
              <video controls :poster="poster">
                <source :src="videoOptions.sources[0].src" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
            <div class="ce-list-item-desc">
              <h6 class="mb-0">{{ content.video.title.slice(0, -4) }}</h6>
            </div>
            <div class="ce-list-item-action">
              <b-button @click="deleteVideo" variant="flat-primary" size="sm"
                ><i class="fas fa-trash"></i
              ></b-button>
            </div>
          </div>
        </div>
      </b-form-group>
    </div>

    <div v-else :class="'ce ce-' + content.type">
      <h3 v-if="content.headline" class="ce-headline mb-1">
        {{ content.headline }}
      </h3>
      <!-- Om videon håller på att bearbetas -->
      <b-alert v-if="state == 'Processing'" show variant="primary" class="mb-2">
        <!-- visas endast om portföljen är satt i läsläge -->
        <h4 class="alert-heading">
          <i class="fas fa-exclamation-triangle"></i> Din video bearbetas..
        </h4>
        <div class="alert-body text-center">
          <p>
            Detta kan ta en liten stund. När videon är klar kommer du att se den
            här.
          </p>
          <b-spinner
            variant="primary"
            class="my-2"
            style="width: 3rem; height: 3rem"
            label="Text Centered"
          />
        </div>
      </b-alert>

      <figure v-else-if="videoSource.length > 1 && state == 'Finished'">
        <video controls :poster="poster">
          <source :src="videoOptions.sources[0].src" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </figure>
    </div>
  </div>
</template>

<script>
import CeButtonGroup from "@/components/Article/ContentElements/CeButtonGroup";
import {
  BFormGroup,
  BFormInput,
  BFormFile,
  BBadge,
  BButton,
  BAlert,
  BSpinner,
} from "bootstrap-vue";
import { createNamespacedHelpers } from "vuex";
import ContentElementButtonGroupMixin from "@/models/mixins/ContentElementButtonGroupMixin";
const { mapActions } = createNamespacedHelpers("editArticle");

export default {
  name: "CeVideo",
  props: {
    content: {},
    editMode: Boolean,
  },
  data() {
    return {
      poster: "",
      video: undefined,
      state: null,
      hasVideoBeenUploaded: false,
      areThereErrorsUploadingVideo: false,
      hasStreamingUrlsBeenFetched: false,
      getStreamingUrlTimeoutFunction: null,
      videoOptions: {
        autoplay: false,
        controls: true,
        fluid: true,
        sources: [
          {
            src: "",
            type: "application/dash+xml",
          },
          {
            src: "",
            type: "application/x-mpegURL",
          },
        ],
      },
    };
  },
  // TODO: make it reload streaming url if it has expired or does not contain sastoken
  mounted() {
    this.$nextTick(function () {
      if (this.content.video) {
        this.hasVideoBeenUploaded = true;
        console.log(this.content.video);
        this.getStreamingUrls();

        console.log(this.content.video.url2);
        if (
          this.content.video.url1 === "NIL" ||
          this.content.video.url2.includes("redaappmediaservice") ||
          this.isSasExpiredOrMissing(this.content.video.url1)
        ) {
          console.log("getStreamingUrls");
          this.getStreamingUrls();
          return;
        }
        this.getStreamingUrls();

        this.hasStreamingUrlsBeenFetched = true;
        this.state = "Finished";
        this.loadVideo();
      }
    });
  },
  watch: {
    video: function (newVideo, oldVideo) {
      if (!newVideo) return;

      if (oldVideo) {
        this.deleteVideo();
      }

      this.state = "Processing";
      this.uploadVideo();
    },
  },
  components: {
    CeButtonGroup,
    BFormGroup,
    BFormInput,
    BFormFile,
    BBadge,
    BButton,
    BAlert,
    BSpinner,
  },
  computed: {
    getVideoState: function () {
      return this.state;
    },
    videoSource: function () {
      return this.videoOptions.sources[0].src;
    },
  },
  methods: {
    ...mapActions([
      "updateContent",
      "addNewChecklistItem",
      "removeChecklistItem",
      "initArticleEdit",
      "clearArticleEdit",
      "fetchVideoLibrary",
    ]),
    isSasExpiredOrMissing(videoUrl) {
      // Parse the URL and extract the query parameters
      console.log(videoUrl);
      const url = new URL(videoUrl);
      const searchParams = new URLSearchParams(url.search);

      // Check if required SAS parameters are missing
      const sasParameters = ["sv", "sig", "se"];
      for (let param of sasParameters) {
        if (!searchParams.has(param)) {
          return true; // SAS token is missing one or more required fields
        }
      }

      // Extract the expiry time (se) from the URL
      const expiryTimeString = searchParams.get("se");
      if (!expiryTimeString) {
        return true; // Expiry time (se) is missing
      }

      // Parse the expiry time from the URL
      const expiryTime = new Date(expiryTimeString);

      // Compare the expiry time with the current time
      const currentTime = new Date();

      // If the current time is greater than the expiry time, SAS token has expired
      if (currentTime > expiryTime) {
        return true; // SAS token is expired
      }

      // If all checks pass, the SAS token is present and not expired
      return false;
    },
    captureThumbnail() {
      const video = this.content.video;

      // Create a canvas element to capture the frame
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw the first frame of the video on the canvas
      const context = canvas.getContext("2d");
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert the canvas to a data URL and set it as the poster
      this.poster = canvas.toDataURL("image/jpeg");
    },
    sweetAlert: function (title, message, icon) {
      this.$swal({
        title: title,
        text: message,
        icon: icon,
        confirmButtonText: "Ok",
        customClass: {
          confirmButton: "btn btn-primary",
        },
        buttonsStyling: false,
      });
    },
    updateHeadline(headline) {
      let content = this.content;
      content.headline = headline;
      this.updateContent(content);
    },
    videoChanged: function (event) {
      this.video = event;
    },
    uploadVideo: function () {
      let content = this.content;
      content.raw = this.video;
      const that = this;
      this.hasVideoBeenUploaded = false;
      this.$store
        .dispatch("uploadVideo", content)
        .then((res, err) => {
          if (err) {
            console.log(err);
            return;
          }

          content.video = res.data;
          that.updateContent(content);
          that.hasVideoBeenUploaded = true;
          that.state = "Processing";
          that.getStreamingUrls();
        })
        .catch((res) => {
          if (res === "FTB1500") {
            this.state = "TooBig";
            this.sweetAlert(
              "Filen är för stor",
              "Filen får vara max 1GB",
              "error"
            );
          } else {
            that.hasVideoBeenUploaded = false;
            this.areThereErrorsUploadingVideo = true;
          }
        });
    },
    deleteVideo: function () {
      this.$store
        .dispatch("deleteVideo", this.content.video)
        .then((res, err) => {
          if (err) {
            console.log(err);
            return;
          }

          this.video = undefined;
          let content = this.content;
          content.video = undefined;
          this.videoOptions.sources[0].src = "";
          this.hasVideoBeenUploaded = true;
          this.hasStreamingUrlsBeenFetched = false;
          this.updateContent(content);
        })
        .catch((e) => {
          console.log(e);
        });
    },
    getStreamingUrls: function () {
      const that = this;
      let content = this.content;
      const article = this.$store.getters.getArticleBySectionId(
        content.section_id
      );

      this.$store
        .dispatch("getStreamingUrls", this.content.video)
        .then(function (res, err) {
          if (err) {
            console.log(err);
            return;
          }

          if (true === that.hasStreamingUrlsBeenFetched) return;

          content.video = res.data;

          if (!that.editMode) {
            that.initArticleEdit(article.id);
          }

          that.updateContent(content);
          that.$store.dispatch("updateArticle", article);

          if (!that.editMode) {
            that.clearArticleEdit();
          }

          that.hasStreamingUrlsBeenFetched = true;
          that.state = "Finished";
          that.loadVideo();
        })
        .catch((e) => {
          if (e.state) {
            that.state = "Processing";
            that.getStreamingUrlTimeoutFunction = setTimeout(() => {
              that.getStreamingUrls();
            }, 10000);
          }
        });
    },
    loadVideo: function () {
      if (this.content.video && this.content.video.url1 !== "NIL") {
        this.videoOptions.sources[0].src = this.content.video.url1;
        this.videoOptions.sources[1].src = this.content.video.url1;
      }
    },
  },
  beforeDestroy() {
    if (this.getStreamingUrlTimeoutFunction) {
      clearTimeout(this.getStreamingUrlTimeoutFunction);
    }
  },
  mixins: [ContentElementButtonGroupMixin],
};
</script>

<style lang="scss" scoped>
@import "src/assets/scss/variables/_variables.scss";
video {
  max-width: 100%;
  background-color: #ccc;
}

.ce-list-item {
  display: flex;
  align-items: center;
  padding: 0.25rem;
  border-bottom: 1px solid #ebe9f1;
}
.ce-list-item:last-child {
  border-bottom: none;
}
.ce-list-item-prefix {
  width: 40%;
  padding: 0.75rem 0;
}
.ce-list-item-prefix img {
  max-width: 12rem;
}
.ce-list-item-desc {
  padding: 0.75rem 0 0.75rem 0.75rem;
}
.ce-list-item-action {
  padding: 0.75rem 0;
}
.ce-list-item-desc {
  margin-right: auto;
}
</style>
